import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { 
    Calendar,
    Trash2,
    ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import { getReleaseStatusColor } from "@/lib/colorsMap";

export function ReleaseDetailsDialog({ 
    open, 
    onOpenChange, 
    selectedRelease, 
    project,
    activeWeekTasks,
    backlogReleaseGroups,
    releases,
    onUpdate,
    onActivate,
    onDeactivate,
    onReopen,
    onDelete,
    onComplete
}) {
    const { axiosInstance } = useAuth();
    const { toast } = useToast();
    const navigate = useNavigate();
    
    const [isEditing, setIsEditing] = useState(false);
    const [releaseName, setReleaseName] = useState("");
    const [startDate, setStartDate] = useState(null);
    const [targetDate, setTargetDate] = useState(null);
    const [actualReleaseDate, setActualReleaseDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (selectedRelease) {
            setReleaseName(selectedRelease.name);
            setStartDate(selectedRelease.startDate ? new Date(selectedRelease.startDate) : null);
            setTargetDate(selectedRelease.targetDate ? new Date(selectedRelease.targetDate) : null);
            setActualReleaseDate(selectedRelease.actualReleaseDate ? new Date(selectedRelease.actualReleaseDate) : null);
            setIsEditing(false);
        }
    }, [selectedRelease]);

    if (!selectedRelease) return null;

    // Calculate release data
    const isActive = startDate && new Date(startDate) <= new Date();
    const isCompleted = selectedRelease.status === 'shipped';
    
    // Get tasks for this release - either from props or calculate from project data
    let releaseTasks = [];
    if (activeWeekTasks.length > 0 || Object.keys(backlogReleaseGroups).length > 0) {
        // Use provided task data
        releaseTasks = isActive 
            ? activeWeekTasks.filter(task => task.release?.id === selectedRelease._id)
            : backlogReleaseGroups[selectedRelease._id]?.tasks || [];
    } else {
        // Calculate from project data
        const allTasks = project?.tasks || [];
        releaseTasks = allTasks.filter(task => task.release?.id === selectedRelease._id);
    }
    
    const completedTasks = releaseTasks.filter(task => task.completedAt);
    const progressPercentage = releaseTasks.length > 0 
        ? Math.round((completedTasks.length / releaseTasks.length) * 100)
        : 0;

    // Calculate days until target
    const daysUntilTarget = targetDate && !isCompleted
        ? Math.ceil((targetDate - new Date()) / (1000 * 60 * 60 * 24))
        : null;
    
    // Check if release was shipped on time
    const wasShippedOnTime = isCompleted && targetDate && actualReleaseDate
        ? new Date(actualReleaseDate) <= new Date(targetDate)
        : null;

    // Get unique assignees count
    const uniqueAssignees = new Set(releaseTasks.map(task => task.assignee?.id).filter(Boolean)).size;

    const handleSave = async () => {
        if (!releaseName.trim()) {
            toast({
                variant: "destructive",
                description: "Release name is required",
            });
            return;
        }

        setIsLoading(true);
        try {
            await axiosInstance.patch(
                `/projects/${project.organizationSlug}/${project.key}/releases/${selectedRelease._id}`,
                {
                    name: releaseName,
                    targetDate,
                    startDate,
                    actualReleaseDate
                }
            );

            onUpdate({
                ...selectedRelease,
                name: releaseName,
                targetDate,
                startDate,
                actualReleaseDate
            });

            setIsEditing(false);
            toast({
                description: "Release updated successfully",
            });
        } catch (error) {
            console.error('Error updating release:', error);
            toast({
                variant: "destructive",
                description: error.response?.data?.message || "Failed to update release",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusChange = async (action) => {
        if (action === 'activate') {
            onActivate(selectedRelease);
            return;
        }

        if (action === 'deactivate') {
            onDeactivate(selectedRelease);
            return;
        }

        if (action === 'reopen') {
            onReopen(selectedRelease);
            return;
        }

        setIsLoading(true);
        try {
            switch (action) {

                case 'complete':
                    onComplete(selectedRelease);
                    return;

                default:
                    break;
            }
        } catch (error) {
            console.error(`Error ${action} release:`, error);
            toast({
                variant: "destructive",
                description: `Failed to ${action} release`,
            });
        } finally {
            setIsLoading(false);
        }
    };


    const statusColor = getReleaseStatusColor(selectedRelease.status);

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-xl">
                            {isEditing ? (
                                <Input
                                    value={releaseName}
                                    onChange={(e) => setReleaseName(e.target.value)}
                                    className="text-xl font-semibold h-auto py-1 px-2 max-w-lg"
                                    placeholder="Release name"
                                    autoFocus
                                />
                            ) : (
                                <span className="cursor-text" onClick={() => setIsEditing(true)}>
                                    {selectedRelease.name}
                                </span>
                            )}
                        </DialogTitle>
                        <DialogDescription className="flex items-center gap-2 mt-2">
                            <Badge 
                                variant="outline"
                                className={cn(
                                    "font-medium",
                                    statusColor.bg,
                                    statusColor.text,
                                    statusColor.border
                                )}
                            >
                                {isCompleted ? 'Shipped' : isActive ? 'Active' : 'Upcoming'}
                            </Badge>
                            {/* Show completion status for shipped releases - subtle */}
                            {wasShippedOnTime === true && (
                                <span className="text-sm text-muted-foreground">
                                    (on time)
                                </span>
                            )}
                            {wasShippedOnTime === false && (
                                <span className="text-sm text-muted-foreground">
                                    (late)
                                </span>
                            )}
                            {/* Show days remaining for active/upcoming releases */}
                            {daysUntilTarget !== null && (
                                <span className={cn(
                                    "text-sm",
                                    daysUntilTarget < 0 ? "text-destructive" : 
                                    daysUntilTarget <= 7 ? "text-amber-600 dark:text-amber-400" : 
                                    "text-muted-foreground"
                                )}>
                                    {daysUntilTarget < 0 
                                        ? `${Math.abs(daysUntilTarget)} days overdue`
                                        : daysUntilTarget === 0
                                        ? "Due today"
                                        : `${daysUntilTarget} days remaining`
                                    }
                                </span>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 mt-6">
                        {/* Progress Overview */}
                        <div className="space-y-2">
                            <div className="flex justify-between items-center text-sm">
                                <button
                                    onClick={() => {
                                        navigate(`/plan/tasks?releases=${selectedRelease._id}`);
                                        onOpenChange(false);
                                    }}
                                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <ExternalLink className="h-3.5 w-3.5" />
                                    <span>View all tasks</span>
                                </button>
                                <span className="font-medium">{completedTasks.length} of {releaseTasks.length} tasks completed</span>
                            </div>
                            <div className="w-full bg-secondary rounded-full h-2 overflow-hidden">
                                <div 
                                    className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
                                    style={{ width: `${progressPercentage}%` }}
                                />
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-4 rounded-lg bg-secondary/50">
                                <div className="text-2xl font-semibold">{progressPercentage}%</div>
                                <p className="text-xs text-muted-foreground">Complete</p>
                            </div>
                            <div className="text-center p-4 rounded-lg bg-secondary/50">
                                <div className="text-2xl font-semibold">{uniqueAssignees}</div>
                                <p className="text-xs text-muted-foreground">Assignees</p>
                            </div>
                            <div className="text-center p-4 rounded-lg bg-secondary/50">
                                <div className="text-2xl font-semibold">{completedTasks.length}</div>
                                <p className="text-xs text-muted-foreground">Done</p>
                            </div>
                        </div>

                        <Separator />

                        {/* Dates Section */}
                        <div className="space-y-4">
                            <h3 className="font-medium">Timeline</h3>
                            
                            <div className="grid gap-3">
                                {/* Start Date */}
                                <div className="flex items-center justify-between py-2">
                                    <span className="text-sm text-muted-foreground">Start Date</span>
                                    {isEditing ? (
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className={cn(
                                                        "w-[180px] justify-start text-left font-normal",
                                                        !startDate && "text-muted-foreground"
                                                    )}
                                                >
                                                    <Calendar className="mr-2 h-4 w-4" />
                                                    {startDate ? format(startDate, "MMM d, yyyy") : "Not set"}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="end">
                                                <CalendarComponent
                                                    mode="single"
                                                    selected={startDate}
                                                    onSelect={setStartDate}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    ) : (
                                        <span className="text-sm font-medium">
                                            {startDate ? format(startDate, "MMM d, yyyy") : "Not set"}
                                        </span>
                                    )}
                                </div>

                                {/* Target Date */}
                                <div className="flex items-center justify-between py-2">
                                    <span className="text-sm text-muted-foreground">Target Date</span>
                                    {isEditing ? (
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className={cn(
                                                        "w-[180px] justify-start text-left font-normal",
                                                        !targetDate && "text-muted-foreground"
                                                    )}
                                                >
                                                    <Calendar className="mr-2 h-4 w-4" />
                                                    {targetDate ? format(targetDate, "MMM d, yyyy") : "Not set"}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="end">
                                                <CalendarComponent
                                                    mode="single"
                                                    selected={targetDate}
                                                    onSelect={setTargetDate}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    ) : (
                                        <span className="text-sm font-medium">
                                            {targetDate ? format(targetDate, "MMM d, yyyy") : "Not set"}
                                        </span>
                                    )}
                                </div>

                                {/* Release Date */}
                                {(isCompleted || isEditing) && (
                                    <div className="flex items-center justify-between py-2">
                                        <span className="text-sm text-muted-foreground">Release Date</span>
                                        {isEditing ? (
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className={cn(
                                                            "w-[180px] justify-start text-left font-normal",
                                                            !actualReleaseDate && "text-muted-foreground"
                                                        )}
                                                    >
                                                        <Calendar className="mr-2 h-4 w-4" />
                                                        {actualReleaseDate ? format(actualReleaseDate, "MMM d, yyyy") : "Not set"}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0" align="end">
                                                    <CalendarComponent
                                                        mode="single"
                                                        selected={actualReleaseDate}
                                                        onSelect={setActualReleaseDate}
                                                        initialFocus
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        ) : (
                                            <span className="text-sm font-medium">
                                                {actualReleaseDate ? format(actualReleaseDate, "MMM d, yyyy") : "Not set"}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <Separator />

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2">
                            {isEditing ? (
                                <>
                                    <Button 
                                        onClick={handleSave}
                                        disabled={isLoading}
                                        className="flex-1"
                                    >
                                        Save Changes
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        onClick={() => {
                                            setReleaseName(selectedRelease.name);
                                            setStartDate(selectedRelease.startDate ? new Date(selectedRelease.startDate) : null);
                                            setTargetDate(selectedRelease.targetDate ? new Date(selectedRelease.targetDate) : null);
                                            setActualReleaseDate(selectedRelease.actualReleaseDate ? new Date(selectedRelease.actualReleaseDate) : null);
                                            setIsEditing(false);
                                        }}
                                        disabled={isLoading}
                                    >
                                        Cancel
                                    </Button>
                                </>
                            ) : (
                                <>
                                    {!isActive && !isCompleted && (
                                        <Button 
                                            onClick={() => handleStatusChange('activate')}
                                            disabled={isLoading}
                                            className="flex-1"
                                        >
                                            Activate Release
                                        </Button>
                                    )}
                                    
                                    {isActive && !isCompleted && (
                                        <>
                                            <Button 
                                                onClick={() => handleStatusChange('complete')}
                                                disabled={isLoading}
                                                variant="default"
                                                className="flex-1"
                                            >
                                                Ship Release
                                            </Button>
                                            <Button 
                                                onClick={() => handleStatusChange('deactivate')}
                                                disabled={isLoading}
                                                variant="outline"
                                            >
                                                Move to Backlog
                                            </Button>
                                        </>
                                    )}
                                    
                                    {isCompleted && (
                                        <Button 
                                            onClick={() => handleStatusChange('reopen')}
                                            disabled={isLoading}
                                            variant="outline"
                                            className="flex-1"
                                        >
                                            Reopen Release
                                        </Button>
                                    )}
                                    
                                    <Button 
                                        onClick={() => setIsEditing(true)}
                                        disabled={isLoading}
                                        variant="outline"
                                    >
                                        Edit Details
                                    </Button>
                                    
                                    <Button 
                                        onClick={() => onDelete(selectedRelease)}
                                        disabled={isLoading}
                                        variant="ghost"
                                        className="text-destructive hover:text-destructive bg-destructive/20 hover:bg-destructive/40"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </>
                            )}
                        </div>

                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}